import React from "react";
import {
  getDriveFileIconLabel,
  isSpreadsheetDriveFile,
  parseSpreadsheetPreview,
} from "../utils/buildPerfectDraftGoogleContext";

const TYPE_TONE = {
  PDF: "bg-red-50 text-red-600",
  XLS: "bg-emerald-50 text-emerald-700",
  CSV: "bg-emerald-50 text-emerald-700",
  DOC: "bg-gray-100 text-gray-700",
  PPT: "bg-amber-50 text-amber-700",
  IMG: "bg-gray-100 text-gray-700",
  MP4: "bg-gray-900 text-white",
  CODE: "bg-gray-100 text-gray-700",
  TXT: "bg-gray-100 text-gray-600",
  DIR: "bg-gray-100 text-gray-700",
  FILE: "bg-gray-100 text-gray-600",
};

/** Document glyph with a type badge, matching the file-list pattern. */
export function DriveFileTypeIcon({ mimeType, name, className = "" }) {
  const label = getDriveFileIconLabel(mimeType, name);
  const tone = TYPE_TONE[label] || TYPE_TONE.FILE;

  return (
    <span
      className={`relative inline-flex h-9 w-7 shrink-0 ${className}`}
      title={label}
      aria-hidden="true"
    >
      <span className="absolute inset-x-0 top-0 h-6 rounded-sm border border-gray-200 bg-white" />
      <span
        className={`absolute inset-x-0 bottom-0 flex h-4 items-center justify-center rounded-sm text-[8px] font-semibold leading-none tracking-wide ${tone}`}
      >
        {label}
      </span>
    </span>
  );
}

export function SpreadsheetPreview({ text }) {
  const { columns, rows, truncated } = parseSpreadsheetPreview(text);

  if (!columns.length) {
    return <p className="text-sm text-gray-500">This spreadsheet is empty.</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        Excel preview · first sheet
      </p>
      <div className="overflow-auto rounded-lg border border-gray-100">
        <table className="w-full min-w-max text-left text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              {columns.map((column, index) => (
                <th
                  key={`${column}-${index}`}
                  className="whitespace-nowrap px-2.5 py-2 font-medium text-gray-500"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.length === 0 ? (
              <tr>
                <td className="px-2.5 py-2 text-gray-400" colSpan={columns.length}>
                  No rows
                </td>
              </tr>
            ) : (
              rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="bg-white">
                  {columns.map((_, colIndex) => (
                    <td
                      key={colIndex}
                      className="max-w-[10rem] truncate whitespace-nowrap px-2.5 py-1.5 text-gray-900"
                    >
                      {row[colIndex] || ""}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {truncated && (
        <p className="text-xs text-gray-400">Showing the first rows and columns.</p>
      )}
    </div>
  );
}

export function DriveFileContentPreview({
  text = "",
  mimeType,
  name,
  error = null,
  loading = false,
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        <div className="h-3 w-1/3 animate-pulse rounded bg-gray-100" />
        <div className="h-16 animate-pulse rounded-lg bg-gray-100" />
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  if (!String(text || "").trim()) {
    if (isSpreadsheetDriveFile(mimeType, name)) {
      return (
        <p className="text-sm text-gray-500">
          Couldn’t build a spreadsheet preview for this Excel file. Open it in Drive to view the workbook.
        </p>
      );
    }
    return <p className="text-sm text-gray-500">No preview text for this file.</p>;
  }

  if (isSpreadsheetDriveFile(mimeType, name)) {
    return <SpreadsheetPreview text={text} />;
  }

  const preview = String(text).slice(0, 4000);
  return (
    <p className="whitespace-pre-wrap break-words text-sm text-gray-900">
      {preview}
      {text.length > preview.length ? "…" : ""}
    </p>
  );
}
